import { arbeitsRang, type Termin } from '@netcon-work/shared'
/**
 * **Die Circle-Pipe – Arbeitswelten als Keile, Zeitfenster als Ringe**
 * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.9,
 * [E-376](../../../../../docs/ENTSCHEIDUNGEN.md#e-376)).
 *
 * Nils' Handzeichnung vom 30.09.2026, in seinen Worten: *„eine «Circle-Pipe» … das man sich genau
 * ein Element holen kann. Sonst muss das aus der mitte wieder raus oder erledigt sein."* Und
 * dazu: *„die Größe des Teils bestimmt sich nach der WIP."*
 *
 * | | |
 * |---|---|
 * | **Winkel** | die Arbeitswelt – Keilbreite nach ihrem WIP-Limit |
 * | **Radius** | das Zeitfenster, innen dringend |
 * | **Mitte** | was in Arbeit ist |
 *
 * ## Die Schleuse
 *
 * In die Mitte kommt nur, wofür **in dieser Welt** Platz ist. Hinaus führen genau zwei Wege:
 * **erledigt** oder **bewusst hinausgeschoben mit einem Wiedersehen**. Das ist nicht Strenge um
 * der Strenge willen, sondern die Antwort auf Nils' eigentliche Diagnose:
 *
 * > *„Ich denke ich habe Angst das es im backlog untergeht daher schon in Bearbeitung gemacht."*
 *
 * „In Arbeit" war ein **Lesezeichen gegen das Vergessen**. Solange der Rückweg nicht
 * vertrauenswürdig ist, hilft keine Grenze – deshalb gehören Schleuse und Wiedersehen zusammen.
 *
 * ## Warum hier gerechnet und nicht auf dem Server
 *
 * Wie beim Daily und beim Tagesplan: Der Bestand liegt im Client ([Leitplanke
 * 2](../../../../../CLAUDE.md)). Eine Welt umzustellen ist ein Neuzeichnen.
 */

export interface CircleAufgabe {
  id: string
  title: string
  status: string
  zeitfenster: string | null
  workspaceId: string | null
  groesse: string | null
  priority?: string | null
  dueAt?: string | null
  updatedAt?: string
  /** Z-20: der wirksame Termin – fehlt er, gilt `dueAt`. */
  termin?: Termin
  sourceCreatedAt?: string | Date | null
  createdAt?: string | Date | null
}

export interface CircleWelt {
  id: string
  label: string
  farbe: string | null
  /**
   * **Die Keilbreite** – ein Gewicht für die Geometrie, keine Regel. Eine Welt ohne Grenze
   * bekommt hier trotzdem eine Zahl, sonst hätte sie einen Keil der Breite null und wäre
   * unerreichbar.
   */
  wip: number
  /**
   * **Die tatsächliche Grenze, oder `null`** (Z-19 AP 19.11).
   *
   * In einer **persönlichen** Arbeitswelt ohne eigenes Limit gilt ausdrücklich **keins**
   * (`wipHerkunft`, Nils 25.08.2026). Das Rad zeigte trotzdem „11 / 7" – es lieh sich das
   * persönliche Limit der Person und meldete eine Überschreitung, die es nach der Regel gar
   * nicht gibt.
   *
   * Das Arbeitspaket sagt ausdrücklich: *„Nur sichtbar machen, nicht die Regel ändern: eine
   * Welt ohne Limit bleibt erlaubt."* Deshalb `null` statt einer Ersatzzahl – und die Schleuse
   * lässt dort weiterhin alles hinein.
   */
  grenze: number | null
}

export interface Ring {
  id: string
  name: string
  /** Kurzform fürs Telefon: „10 Tage" deckte dort den halben Nachbarkeil zu. */
  kurz: string
}

/**
 * **Die Ringe von innen nach außen.**
 *
 * Dieselben Stufen wie die Zeitfenster überall sonst, plus `offen` für alles ohne Angabe. Das
 * ist bewusst der **äußerste** Ring und nicht die Mitte: Im Rad ist „außen" gleichbedeutend mit
 * „noch nicht dran", und eine Aufgabe ohne Zeitfenster ist genau das – noch nicht eingeordnet.
 *
 * > Das ist **nicht** derselbe Platz, den `DRINGLICHKEIT_RANG` dem Fehlen einer Frist im Vorlauf
 * > gibt ([E-124](../../../../../docs/ENTSCHEIDUNGEN.md): dort steht „ohne" in der Mitte). Der
 * > Unterschied ist Absicht: Der Vorlauf **ordnet**, das Rad **zeigt**. Wer im Rad nach außen
 * > sieht, fragt „was ist noch nicht eingeplant?" – und will es beisammen haben.
 */
export const CIRCLE_RINGE: Ring[] = [
  { id: 'jetzt', name: 'jetzt', kurz: 'jetzt' },
  { id: 'tage5', name: '5 Tage', kurz: '5 T' },
  { id: 'tage10', name: '10 Tage', kurz: '10 T' },
  { id: 'monat', name: 'Monat', kurz: 'Mon' },
  { id: 'offen', name: 'offen', kurz: 'offen' },
]

/** Welcher Ring gilt – alles Unbekannte fällt nach außen, statt zu verschwinden. */
export function ringVon(zeitfenster: string | null): string {
  if (zeitfenster === 'quartal') return 'offen'
  return CIRCLE_RINGE.some((r) => r.id === zeitfenster) ? (zeitfenster as string) : 'offen'
}

export interface Zelle {
  weltId: string
  ringId: string
  aufgaben: CircleAufgabe[]
}

export interface WeltKeil {
  welt: CircleWelt
  /** Anteil am Vollkreis, 0–1. */
  anteil: number
  zellen: Zelle[]
  inArbeit: CircleAufgabe[]
  /** Ist diese Welt über ihrem Limit? Dann ist der Keil rot beschriftet. */
  ueber: boolean
}

export interface CircleLage {
  keile: WeltKeil[]
  wipSumme: number
  inArbeitGesamt: number
  /**
   * **Die Ringe, in denen wirklich etwas liegt** – in der Reihenfolge von innen nach außen
   * (Nils, 30.09.2026: *„leere zeitfenster ausblenden das alles immer an den inneren Kreis
   * rückt in der richtigen Reihenfolge"*).
   *
   * `zellen` an jedem Keil ist auf genau diese Ringe beschränkt und in derselben Reihenfolge –
   * Ring `i` der Lage gehört zu Zelle `i` jedes Keils.
   */
  ringe: Ring[]
  /**
   * **Liegt mindestens eine Welt ohne Grenze im Rad?** Dann ist „n von N" eine Behauptung, die
   * so nicht stimmt – die Nabe sagt dann nur die Zahl.
   */
  ohneGrenze: boolean
}

/**
 * **Was in einen Ring gehört** – dieselbe Grenze wie im Vorlauf.
 *
 * `warten` fehlte hier (Nils, 01.10.2026, im Rad gesehen). Eine Aufgabe, die auf eine Antwort
 * wartet, ist **kein Vorlauf**: Der Vorlauf blendet sie bis zu ihrem Wiedervorlagetag aus
 * ([E-357](../../../../../docs/ENTSCHEIDUNGEN.md#e-357)) – als Vorschlag für etwas, an dem man
 * gerade nichts tun kann, wäre sie nur Rauschen. Dass sie im Rad trotzdem einen Platz bekam,
 * war schlicht eine vergessene Zeile.
 */
const OFFEN = (a: CircleAufgabe) =>
  a.status !== 'erledigt' &&
  a.status !== 'zurueckgestellt' &&
  a.status !== 'review' &&
  a.status !== 'warten'

/**
 * **Das Rad aus dem Bestand rechnen.**
 *
 * Welten **ohne** WIP-Limit bekommen `wip: 1`, damit sie überhaupt einen Keil haben – ein Keil
 * der Breite null wäre unsichtbar und die Welt damit unerreichbar. Das ist eine Darstellungs-
 * entscheidung, keine Regel: Die Schleuse fragt getrennt, ob Platz ist.
 *
 * ## Leere Welten bekommen keinen Keil (Nils, 30.09.2026)
 *
 * *„wenn ein workspace leer ist, den im circle nicht anzeigen, so ist platz für nur die die
 * auch einen realen vorlauf haben."*
 *
 * Der Winkel ist ein **knappes Gut**: Jeder Keil nimmt den anderen Platz weg, und eine Welt
 * ohne eine einzige Aufgabe zeigt nichts an, wofür sich Hinsehen lohnt. Wer fünf Welten hat
 * und in zweien arbeitet, bekam bisher fünf Keile – drei davon leer, die zwei vollen dafür
 * halb so breit.
 *
 * **Die Grenze ist „leer“, nicht „ohne Vorlauf“** – und das ist Absicht. Eine Welt, in der
 * etwas **in Arbeit** ist, bleibt stehen, auch wenn ihr Vorlauf leer ist: Ihr Keil zu streichen
 * hieße, laufende Arbeit verschwinden zu lassen, und die Zahl in der Mitte (`inArbeitGesamt`)
 * zählt über die Keile – sie würde stillschweigend falsch. Genau die Aufgabe, an der man
 * gerade sitzt, darf das Rad nicht verschweigen.
 *
 * **Bleibt am Ende nichts übrig**, gibt es auch keine Keile; das Bauteil zeigt dann den leeren
 * Zustand statt eines Rades aus einem einzigen Vollkreis ohne Inhalt.
 */
export function circleLage(
  aufgaben: readonly CircleAufgabe[],
  welten: readonly CircleWelt[],
  /** Für die Rangfolge (überfällig, heute fällig) – hereingegeben, nicht gelesen. */
  stichtag: Date = new Date(),
): CircleLage {
  /** Z-20: dieselbe Rangfolge wie Vorlauf und Kalender (`arbeitsRang`). */
  const rang = arbeitsRang(null, stichtag)
  const belegt = (welt: CircleWelt) =>
    aufgaben.some((a) => a.workspaceId === welt.id && (a.status === 'in_arbeit' || OFFEN(a)))
  const nutzbar = welten.filter((w) => w.wip > 0 && belegt(w))
  /** Die Keilbreiten – Geometrie, jede Welt trägt hier eine Zahl. */
  const breitenSumme = nutzbar.reduce((s, w) => s + w.wip, 0) || 1
  /**
   * **Die Summe der echten Grenzen** – nur Welten, die eine haben. Eine Welt ohne Grenze in
   * diese Zahl einzurechnen würde eine Kapazität behaupten, die niemand festgelegt hat.
   */
  const wipSumme = nutzbar.reduce((s, w) => s + (w.grenze ?? 0), 0)

  /**
   * **Ein Ring, in dem nirgends etwas liegt, wird nicht gezeichnet** (Nils, 30.09.2026).
   *
   * Vorher waren es immer fünf. Wer nichts mit Zeitfenster „Monat" hat, bekam trotzdem einen
   * Ring dafür – ein leerer Gurt, der die dringenden Sachen weiter nach außen schob. Genau
   * verkehrt herum: Die Mitte soll dicht besetzt sein, denn von dort holt man nach.
   *
   * Geprüft wird über **alle** Welten hinweg, nicht je Keil. Ringe sind Kreise; einen Ring in
   * einem Keil wegzulassen und im nächsten zu behalten, ginge geometrisch gar nicht – die
   * Radien müssen für das ganze Rad dieselben sein.
   */
  const benutzt = new Set(
    aufgaben
      .filter(
        (a) => a.status !== 'in_arbeit' && OFFEN(a) && nutzbar.some((w) => w.id === a.workspaceId),
      )
      .map((a) => ringVon(a.zeitfenster)),
  )
  const ringe = CIRCLE_RINGE.filter((r) => benutzt.has(r.id))

  const keile = nutzbar.map((welt) => {
    const meine = aufgaben.filter((a) => a.workspaceId === welt.id)
    const inArbeit = meine.filter((a) => a.status === 'in_arbeit').sort(rang)
    /**
     * **Dieselbe Reihenfolge wie im Vorlauf** (Nils, 01.10.2026). Bis hierher sortierte das Rad
     * überhaupt nicht – es nahm die Aufgaben so, wie sie zufällig im Bestand standen, bei
     * jedem Neuladen anders. Der Ring vertritt dabei den Schritt „Zeitfenster“ der vollen
     * Rangfolge; was danach kommt, ist `arbeitsRang` – seit dem 06.10.2026 mit dem wirksamen
     * Termin, wie im Vorlauf (Z-20).
     */
    const zellen = ringe.map((ring) => ({
      weltId: welt.id,
      ringId: ring.id,
      aufgaben: meine
        .filter((a) => a.status !== 'in_arbeit' && OFFEN(a) && ringVon(a.zeitfenster) === ring.id)
        .sort(rang),
    }))
    return {
      welt,
      anteil: welt.wip / breitenSumme,
      zellen,
      inArbeit,
      /* Ohne Grenze gibt es kein „darüber" – das ist die Regel, nicht ihre Umgehung. */
      ueber: welt.grenze !== null && inArbeit.length > welt.grenze,
    }
  })

  return {
    keile,
    wipSumme,
    inArbeitGesamt: keile.reduce((s, k) => s + k.inArbeit.length, 0),
    ringe,
    ohneGrenze: nutzbar.some((w) => w.grenze === null),
  }
}

/**
 * **Darf diese Aufgabe in die Mitte?**
 *
 * Gezählt wird **je Welt**, nicht über alles: Ein voller Entwicklungs-Fokus darf das Abhaken
 * einer privaten Kleinigkeit nicht sperren – dieselbe Überlegung wie bei `wipHerkunft` in
 * `shared/workspaces.ts`, wo persönliche Welten aus der geteilten Rechnung herausgehalten werden.
 */
export function platzInDerMitte(keil: WeltKeil): {
  frei: boolean
  belegt: number
  grenze: number | null
} {
  const grenze = keil.welt.grenze
  return {
    /* `null` heißt: keine Grenze. Dann ist immer Platz – das ist Nils' Regel vom 25.08.2026. */
    frei: grenze === null || keil.inArbeit.length < grenze,
    belegt: keil.inArbeit.length,
    grenze,
  }
}
